import React from 'react';
import { arrayOf, bool, func, string } from 'prop-types';
import { change, Field, Form, reduxForm } from 'redux-form';
import { Button, Col, FormControl } from 'react-bootstrap';

import FontAwesome from 'react-fontawesome';

import { SELECT_OPTION_SHAPE } from 'utils/customPropTypes';
import Select from 'components/common/Select';
import FieldWrapper from 'components/common/FieldWrapper';
import DuplicatePartnerModal from './DuplicatePartnerModal';

import { firstPageValidations, asyncValidate } from './validations';

export function FirstPage({
  country,
  countryOptions,
  dispatch,
  entityTypeOptions,
  handleModalClose,
  handleNextPageClick,
  handleSubmit,
  referral,
  referralOptions,
  serviceCountryOptions,
  showModal,
  stateOptions
}) {
  const IS_US_COUNTRY = country !== 'United States';
  const clearAddressAndZipOnNonUsCountry = (value) => {
    if (value !== 'United States') {
      dispatch(change('partnerSubmissionForm', 'state', ''));
      dispatch(change('partnerSubmissionForm', 'zip_code', ''));
    }
    return value;
  };

  const clearAsyncErrorsFor = (field) => (value, _, allValues) => {
    dispatch(change('partnerSubmissionForm', field, allValues[field]));
    return value;
  };

  return (
    <div className="mobile-container">
      <Form onSubmit={handleSubmit}>
        <Col xs={12}>
          <h2 id="partner-contact-form-heading">
            Affiliate/Publisher Interest Questionnaire
            <small className="smaller block">
              {'Thanks for your interest in joining our Partner Network. ' +
                'Please fill out the below information and we will be in contact with you shortly.'}
            </small>
          </h2>
        </Col>
        <Col xs={12}>
          <hr />
          <fieldset className="with-legend">
            <legend>Business Information</legend>

            <Col md={6}>
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '1' }}
                label="Business Name"
                name="name"
                required
                showLabel
              />
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '2' }}
                label="Legal Name (for agreements)"
                name="legal_name"
                required
                showLabel
              />
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '3' }}
                label="Business Phone"
                name="business_phone"
                required
                showLabel
              />
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={Select}
                inputComponentProps={{
                  isClearable: false,
                  options: entityTypeOptions,
                  placeholder: 'Select type',
                  tabIndex: '4'
                }}
                label="Type of entity"
                name="entity_type"
                required
                showLabel
              />
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '5' }}
                label="Website"
                name="website"
                showLabel
              />
            </Col>
            <Col md={6}>
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={Select}
                inputComponentProps={{
                  isClearable: false,
                  options: serviceCountryOptions,
                  placeholder: 'Select country of service',
                  tabIndex: '11'
                }}
                label="Country of Service (for currency)"
                name="service_country_code"
                required
                showLabel
              />
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={Select}
                inputComponentProps={{
                  isClearable: false,
                  options: countryOptions,
                  placeholder: 'Select country',
                  tabIndex: '6',
                  type: 'text'
                }}
                label="Address Country"
                name="country"
                normalize={clearAddressAndZipOnNonUsCountry}
                required
                showLabel
              />
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '7' }}
                label="Address 1"
                name="address_1"
                required
                showLabel
              />
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '8' }}
                label="Address 2"
                name="address_2"
                showLabel
              />
              <Col xs={4} className="state-select-container">
                <Field
                  component={FieldWrapper}
                  hideLabelInError
                  inputComponent={Select}
                  inputComponentProps={{
                    isClearable: false,
                    disabled: IS_US_COUNTRY,
                    placeholder: IS_US_COUNTRY ? '' : 'Select state',
                    options: stateOptions,
                    simpleValue: true,
                    tabIndex: '9'
                  }}
                  label="State"
                  name="state"
                  normalize={clearAsyncErrorsFor('zip_code')}
                  showLabel
                />
              </Col>
              <Col xs={8}>
                <Field
                  component={FieldWrapper}
                  hideLabelInError
                  inputComponent={FormControl}
                  inputComponentProps={{
                    disabled: IS_US_COUNTRY,
                    tabIndex: '10'
                  }}
                  label="Zip Code"
                  name="zip_code"
                  normalize={clearAsyncErrorsFor('state')}
                  showLabel
                />
              </Col>
            </Col>
          </fieldset>
        </Col>
        <Col xs={12}>
          <fieldset className="with-legend">
            <legend>Contact Information</legend>
            <Col md={6}>
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '12' }}
                label="Name"
                name="primary_contact_name"
                required
                showLabel
              />
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '13' }}
                label="Email"
                name="primary_contact_email"
                required
                showLabel
              />
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '14' }}
                label="Title"
                name="primary_contact_title"
                showLabel
              />
            </Col>
            <Col md={6}>
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '15' }}
                label="Office Phone"
                name="primary_contact_office_phone"
                showLabel
              />
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '16' }}
                label="Mobile Phone"
                name="primary_contact_mobile_phone"
                showLabel
              />
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={FormControl}
                inputComponentProps={{ tabIndex: '17' }}
                label="Skype"
                name="primary_contact_skype_name"
                showLabel
              />
            </Col>
          </fieldset>
        </Col>

        <Col xs={12}>
          <fieldset className="with-legend">
            <legend>Additional Information</legend>
            <Col md={6}>
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={Select}
                inputComponentProps={{
                  isClearable: false,
                  options: [
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' }
                  ],
                  placeholder: 'Select an option',
                  tabIndex: '18',
                  type: 'text'
                }}
                label="Are you currently running any pay per call campaigns as an affiliate now?"
                name="experienced"
                required
                showLabel
              />
            </Col>
            <Col md={6}>
              <Field
                component={FieldWrapper}
                hideLabelInError
                inputComponent={Select}
                inputComponentProps={{
                  isClearable: false,
                  isCreatable: true,
                  options: [
                    ...referralOptions,
                    { value: referral, label: referral }
                  ],
                  placeholder: 'Select from list or type other selection',
                  tabIndex: '19',
                  type: 'text'
                }}
                label="How did you hear about eLocal?"
                name="referral"
                required
                showLabel
              />
            </Col>
          </fieldset>
        </Col>
        <Col xs={12} className="margin-bottomgap20 text-center">
          {/* eslint-disable-next-line jsx-a11y/tabindex-no-positive */}
          <Button
            bsStyle="warning"
            className="pull-right"
            tabIndex="20"
            type="submit"
            onClick={handleNextPageClick}
          >
            {'Next Step '}
            <FontAwesome name="chevron-right" tag="i" />
          </Button>
        </Col>
      </Form>
      <DuplicatePartnerModal
        handleClose={handleModalClose}
        showModal={showModal}
      />
    </div>
  );
}

FirstPage.propTypes = {
  country: string,
  countryOptions: arrayOf(SELECT_OPTION_SHAPE).isRequired,
  dispatch: func.isRequired,
  entityTypeOptions: arrayOf(SELECT_OPTION_SHAPE).isRequired,
  handleModalClose: func.isRequired,
  handleNextPageClick: func.isRequired,
  handleSubmit: func.isRequired,
  referral: string,
  referralOptions: arrayOf(SELECT_OPTION_SHAPE).isRequired,
  serviceCountryOptions: arrayOf(SELECT_OPTION_SHAPE).isRequired,
  showModal: bool.isRequired,
  stateOptions: arrayOf(SELECT_OPTION_SHAPE).isRequired
};

FirstPage.defaultProps = {
  country: undefined,
  referral: undefined
};

export default reduxForm({
  destroyOnUnmount: false,
  form: 'partnerSubmissionForm',
  forceUnregisterOnUnmount: true,
  validate: firstPageValidations,
  asyncValidate,
  asyncBlurFields: [
    'business_phone',
    'primary_contact_email',
    'state',
    'zip_code'
  ]
})(FirstPage);
